<template>
  <div class="upload-image">
    <h4 class="label">
      {{ $t('components.PostImage.addImage') }}
      <span v-show="showRequiredOrOptionalLabel">{{ includesDocomo ? $t('labels.requiredLabel'): $t('labels.optionalLabel') }}</span>
      （{{ post.postImages.length }}/1{{ $t('components.PostImage.unit') }}）
    </h4>

    <div v-show="loading" class="upload-image__wrapper loading" :class="{ selected: post.postImages.length }">
      <Loading />
    </div>

    <div v-show="!loading" class="upload-image__wrapper" :class="{ selected: post.postImages.length }">
      <div v-for="photo in post.postImages" :key="photo.id" class="upload-image__content">
        <img :src="photo.url" :alt="photo.name" class="upload-image__image">

        <div class="c-button c-button--icon">
          <button class="c-button__item" @click="removePhoto(photo.id)">
            <img class="c-button__icon" :src="imgIconCross" alt="">
            削除する
          </button>
        </div>
      </div>
    </div>

    <ValidationObserver ref="postImageObserver">
      <ValidationProvider
        v-slot="{ errors }"
        :model-value="selectedFile"
        :rules="postImageRules"
        :name="$t('components.PostImage.image')"
      >
        <div class="c-button c-button--icon">
          <label class="c-button__item c-button__item--primary">
            <input
              ref="file"
              type="file"
              class="u-hidden"
              accept="image/jpeg,image/png"
              :disabled="loading || post.postImages.length === 1"
              @change="handleFileChange"
            >
            <img class="c-button__icon" :src="imgIconPlusRed" alt="">
            {{ $t('components.PostImage.selectImage') }}
          </label>
        </div>
        <KkTooltip
          v-if="showTooltip"
          :label="$t('components.PostImage.tooltip.title')"
          top="24px"
          left="0"
          width="400px"
          font-size="12px"
          class="upload-image__tooltip"
          :is-visible="showPostImageTooltip"
          @showTooltip="showPostImageTooltip = true"
          @hideTooltip="showPostImageTooltip = false"
        >
          <p class="whiteSpacePreWrap">
            <span class="boldText">{{ $t('components.PostImage.tooltip.limit') }}</span><br>
            {{ $t('components.PostImage.tooltip.limitText', { number: maxImageNum }) }}<br><br>
            <span class="boldText">{{ $t('components.PostImage.tooltip.fileType') }}</span><br>
            {{ availableImageTypesText }}<br><br>
            <span class="boldText">{{ $t('components.PostImage.tooltip.imageSize') }}</span><br>
            {{ $t('components.PostImage.tooltip.imageSizeText', {
              width: minImageWidth,
              height: minImageHeight,
              size: maxImageSize / 1000
            }) }}
          </p>
        </KkTooltip>

        <ValidationError
          id="postImagesError"
          :error="errors[0]"
        />
        <ValidationError
          id="maxLengthError"
          :error="maxLengthError"
        />
      </ValidationProvider>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import ImgIconPlusRed from '../../../../images/common/icon_plus_red.svg'
import ImgIconCross from '../../../../images/common/icon_cross.svg'
import ImgIconCheck from '../../../../images/common/icon_check.svg'
import ValidationError from '../ValidationError'
import Loading from '../../components/Loading.vue'
import { KkTooltip } from '../../components/base'
import { maxPostImageNum, availablePostImageTypes, minPostImageWidth, minPostImageHeight, maxImageMbSize } from '../../domains/post/consts'

export default {
  name: 'PostImage',
  components: {
    ValidationError,
    Loading,
    KkTooltip
  },
  data() {
    return {
      loading: false,
      maxLengthError: '',
      showPostImageTooltip: false,
      selectedFile: null
    }
  },
  computed: {
    ...mapState('companiesStorePosts', ['post']),
    imgIconPlusRed() {
      return ImgIconPlusRed
    },
    imgIconCross() {
      return ImgIconCross
    },
    imgIconCheck() {
      return ImgIconCheck
    },
    showTooltip() {
      return this.post.services.length > 0
    },
    maxImageNum() {
      let minLimit = Number.MAX_VALUE
      this.post.services.forEach(service => {
        if (maxPostImageNum[service] < minLimit) {
          minLimit = maxPostImageNum[service]
        }
      })
      return minLimit
    },
    // 選択されているサービスに共通の画像タイプのリスト
    availableImageTypes() {
      // 選択されたサービスごとの画像タイプのリストを取得
      const imageTypesForSelectedServices = this.post.services.map(service => {
        return availablePostImageTypes[service]
      })
      if (imageTypesForSelectedServices.length === 0) {
        return []
      }

      // 最初のサービスの画像タイプリストを基準として、共通のものを抽出
      return imageTypesForSelectedServices.reduce((commonTypes, types) => {
        return commonTypes.filter(type => types.includes(type))
      }, imageTypesForSelectedServices[0])
    },
    availableImageTypesText() {
      return this.availableImageTypes.join(',')
    },
    minImageWidth() {
      let maxWidth = 0
      this.post.services.forEach(service => {
        if (minPostImageWidth[service] > maxWidth) {
          maxWidth = minPostImageWidth[service]
        }
      })
      return maxWidth
    },
    minImageHeight() {
      let maxHeight = 0
      this.post.services.forEach(service => {
        if (minPostImageHeight[service] > maxHeight) {
          maxHeight = minPostImageHeight[service]
        }
      })
      return maxHeight
    },
    maxImageSize() {
      let minLimit = Number.MAX_VALUE
      this.post.services.forEach(service => {
        if (maxImageMbSize[service] < minLimit) {
          minLimit = maxImageMbSize[service]
        }
      })
      return minLimit
    },
    postImageRules() {
      let baseRules = `image|postImage:${this.minImageWidth},${this.minImageHeight}|size:${this.maxImageSize}`

      // ドコモ投稿で既存のpostImageがない場合はrequiredを追加
      if (this.includesDocomo && !this.post.postImages.length) {
        // 既存のpostImageがない場合のみrequiredを追加
        baseRules = `required|${baseRules}`
      }
      return baseRules
    },
    isOnlyGoogle() {
      return this.post.services.length === 1 && this.post.services.includes('google_map')
    },
    showRequiredOrOptionalLabel() {
      // 投稿タイプが最新情報でgoogleのみ選択している場合は、必須・任意ラベルを表示しない
      if (this.post.topicType === 'STANDARD' && this.isOnlyGoogle) return false

      return true
    },
    includesDocomo() {
      if (!this.post.services) return false
      return this.post.services.includes('docomo')
    },
  },
  methods: {
    ...mapActions('companiesStorePosts', [
      'postPostImage',
      'deletePostImage'
    ]),
    async dropPhotos(event) {
      if(this.loading) return

      this.loading = true

      const { valid } = await this.$refs.postImageObserver.validate()
      if (!valid) {
        this.loading = false
        return
      }
      let files = Array.from(event.target.files)
      const allFilesLength = this.post.postImages.length + files.length

      this.maxLengthError = ''

      if (allFilesLength > 1) {
        this.maxLengthError = 'アップロードできる画像は1つだけです'
        this.loading = false
        return
      }

      await Promise.all(files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader()

          reader.readAsDataURL(file)

          reader.onload = async () => {
            const payload = {
              companyUuid: this.$route.params.company_uuid,
              fileName: file.name,
              postImage: reader.result
            }
            await this.postPostImage(payload)
            resolve()
          }
        })
      }))

      this.loading = false
    },
    async removePhoto(id) {
      this.loading = true
      const payload = {
        companyUuid: this.$route.params.company_uuid,
        id: id,
      }
      await this.deletePostImage(payload)
      this.selectedFile = null

      this.loading = false
    },
    async handleFileChange(event) {
      if (event.target.files.length > 0) {
        this.selectedFile = event.target.files[0]

        // this.$refs.postImageObserver.validate()の内容が更新されてからdropPhotosしたいのでnextTickを使用
        await this.$nextTick()
        this.dropPhotos(event)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@media screen and (max-width: 768px) {
  .upload-image__wrapper {
    overflow: scroll hidden;
  }
}
.upload-image {
  &__wrapper {
    display: flex;
    width: 100%;
    height: 250px;
    padding: 24px;
    background-color: rgba(245, 245, 245, 0.5);
    border: 1px solid #E4E7EB;
    border-radius: 4px;
    &.loading {
      display: block;
    }

    &.selected {
      background-color: #ffffff;
    }
  }

  &__content {
    flex: none;
    width: calc(176px + 24px);
    height: 176px;
    padding-right: 24px;

    .c-button {
      margin: 0;
    }

    .c-button__item {
      padding: 0;
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    margin-bottom: 8px;
    object-fit: cover;
    border: 1px solid #E3E3E3;
    border-radius: 4px;
  }

  &__tooltip {
    font-size: 12px;
    .whiteSpacePreWrap {
      white-space: pre-wrap;
    }
    .boldText {
      font-weight: bold;
    }
  }
}
:deep(.info__text) {
  white-space: unset;
}
.label {
  font-size: 12px;
  margin: 32px 0 8px;
}
</style>
